import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowLeft, FileSpreadsheet, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/FilterBar";
import { Panel } from "@/components/KpiCard";
import { PERSO, PeriodPicker } from "@/components/PeriodPicker";
import { OperationBlock } from "@/components/OperationBlock";
import { PlanComptableButton } from "@/components/PlanComptableButton";
import { ResumeCommentaires } from "@/components/ResumeCommentaires";
import { LotBar } from "@/components/LotBar";
import { LocatairesPanel } from "@/components/LocatairesPanel";
import { LoyersManquantsPanel } from "@/components/LoyersManquantsPanel";
import { PayeursPanel } from "@/components/PayeursPanel";
import { FournisseursPanel } from "@/components/FournisseursPanel";
import { RecusLoyerPanel } from "@/components/RecusLoyerPanel";
import { DoublonsPanel } from "@/components/DoublonsPanel";
import { ConciliationTaxesPanel } from "@/components/ConciliationTaxesPanel";
import { TropPayesPanel } from "@/components/TropPayesPanel";
import { AcomptesTaxesPanel } from "@/components/AcomptesTaxesPanel";
import { EtatsRqPanel } from "@/components/EtatsRqPanel";
import { Checkbox } from "@/components/ui/checkbox";
import { api, download, errMsg, money } from "@/lib/api";

const params = (p) => (p.mode === PERSO ? (p.debut && p.fin ? { debut: p.debut, fin: p.fin } : null) : { mois: p.mode });

const fermerOnglet = () => {
  window.close();
  setTimeout(() => { if (!window.closed) { toast.info("Votre navigateur n'a pas permis de fermer cet onglet : fermez-le, ou revenez à l'onglet du tableau de bord."); } }, 300);
};

// Bandeau quand on arrive depuis « Voir » du tableau de bord : ferme l'onglet pour revenir à la catégorie ouverte
const RetourTableau = ({ libelle }) => (
  <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3" data-testid="retour-tableau-bandeau">
    <p className="text-sm text-emerald-900">Ouvert depuis le tableau de bord : {libelle}</p>
    <Button size="sm" onClick={fermerOnglet} className="bg-emerald-700 hover:bg-emerald-800" data-testid="retour-tableau-btn">
      <ArrowLeft className="h-3.5 w-3.5 mr-1" />Revenir à {libelle} du tableau de bord
    </Button>
  </div>
);

const Exporter = ({ filtre, titre }) => {
  const [busy, setBusy] = useState(null);
  const dl = async (format) => {
    setBusy(format);
    try { await download("/commentaires/export", { format, ...params(filtre) }, `${titre}.${format}`); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
  };
  return (
    <div className="flex gap-2">
      <Button variant="outline" onClick={() => dl("xlsx")} disabled={!!busy} data-testid="commentaires-export-xlsx">
        {busy === "xlsx" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 mr-2" />}Excel
      </Button>
      <Button variant="outline" onClick={() => dl("pdf")} disabled={!!busy} data-testid="commentaires-export-pdf">
        {busy === "pdf" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileText className="h-4 w-4 mr-2" />}PDF
      </Button>
    </div>
  );
};

export default function Commentaires() {
  const [query] = useSearchParams();
  const cible = query.get("op");
  const [mois, setMois] = useState([]);
  const [filtre, setFiltre] = useState(null);
  const [plan, setPlan] = useState({ comptes: [] });
  const [data, setData] = useState(null);
  const [choix, setChoix] = useState({ categories: [], logements: [] });
  const [coches, setCoches] = useState([]);
  useEffect(() => {
    api.get("/mois").then(({ data: m }) => {
      setMois(m);
      setFiltre(m.includes(query.get("mois")) ? { mode: query.get("mois") } : m.length ? { mode: m[0] } : { mode: PERSO });
    }).catch((e) => toast.error(errMsg(e)));
    api.get("/plan-comptable").then((r) => setPlan(r.data)).catch(() => {});
    Promise.all([api.get("/settings"), api.get("/logements/config"), api.get("/logements")]).then(([s, c, l]) => setChoix({
      categories: [...new Set([...Object.keys(s.data.categories), "Autres"])],
      logements: [...new Set([...c.data.map((g) => g.nom), ...l.data])],
    })).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const charger = useCallback((vider) => {
    const p = filtre && params(filtre);
    if (vider) setData(null);
    if (!p) return;
    api.get("/commentaires", { params: p }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [filtre]);
  useEffect(() => { charger(true); setCoches([]); }, [charger]);
  useEffect(() => {
    const el = cible && data && document.querySelector(`[data-testid="op-${cible}"]`);
    if (!el) { return undefined; }
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.classList.add("ring-2", "ring-emerald-500", "ring-offset-2");
    const fin = setTimeout(() => el.classList.remove("ring-2", "ring-emerald-500", "ring-offset-2"), 4000);
    return () => clearTimeout(fin);
  }, [cible, data]);
  const [version, setVersion] = useState(0);
  const [vConc, setVConc] = useState(0);
  const [vSuivi, setVSuivi] = useState(0);
  const onSaved = () => { charger(false); setVersion((v) => v + 1); };
  const onCoche = (id, v) => setCoches((c) => (v ? [...c, id] : c.filter((x) => x !== id)));
  const tous = data && data.operations.length > 0 && coches.length === data.operations.length;
  const parCle = {};
  (data?.operations || []).forEach((o) => { parCle[o.cle] = [...(parCle[o.cle] || []), o.id]; });
  const onSemblables = (k) => setCoches((c) => [...new Set([...c, ...(parCle[k] || [])])]);
  return (
    <div data-testid="commentaires-page">
      <PageHeader eyebrow="Relevés Desjardins" title="Commentaires sur opérations">
        {data && <Exporter filtre={filtre} titre={data.titre} />}
      </PageHeader>
      {query.get("retour") && <RetourTableau libelle={query.get("retour")} />}
      <div className="flex flex-col lg:flex-row lg:items-center gap-3 mb-6">
        {filtre && <PeriodPicker value={filtre} onChange={setFiltre} mois={mois} sansTout />}
        <PlanComptableButton plan={plan} onImported={setPlan} />
      </div>
      {!data ? (filtre && !params(filtre) ? <p className="text-sm text-slate-500" data-testid="commentaires-choisir-dates">Choisissez la date de début et la date de fin.</p> : <Loader2 className="h-6 w-6 animate-spin text-emerald-700" />) : (
        <Panel title={data.titre} testId="commentaires-liste"
          action={<span className="text-xs font-mono text-slate-600" data-testid="commentaires-totaux">{data.operations.length} op. · Retraits {money(data.total_debit)} · Dépôts {money(data.total_credit)} · TPS {money(data.total_tps)} · TVQ {money(data.total_tvq)}{data.nb_doublons ? ` · ${data.nb_doublons} op. en double exclues` : ""}</span>}>
          <p className="text-xs text-slate-500 mb-3">TPS et TVQ calculées automatiquement (taxes incluses : ÷ 1,14975), en négatif pour les retraits. Cochez « Non taxable » pour les mettre à 0, ou « TPS seul. » / « TVQ seul. » quand le fournisseur ne facture qu'une des deux taxes (l'autre passe à 0) ; videz une case pour revenir au calcul automatique. Immeubles non taxables : le calcul va au début du commentaire (non créditable) ; cochez « Taxable » quand une dépense de ces immeubles comporte quand même des taxes créditables. Chaque choix est mémorisé et reproposé pour la même opération les autres mois (en italique).</p>
          {!plan.comptes.length && <p className="text-xs text-amber-700 mb-3" data-testid="plan-comptable-vide">Importez votre plan comptable pour choisir le compte de chaque opération.</p>}
          {data.operations.length === 0 ? <p className="text-sm text-slate-500" data-testid="commentaires-vide">Aucune opération pour cette période.</p>
            : <>
              {coches.length > 0 && <LotBar ids={coches} comptes={plan.comptes} categories={choix.categories} logements={choix.logements}
                onDone={() => { setCoches([]); charger(false); }} onClear={() => setCoches([])} />}
              <div className="hidden sm:grid sticky top-[108px] lg:top-0 z-20 bg-white/95 backdrop-blur pt-2 grid-cols-[124px_52px_1fr_110px_110px_100px_100px_112px] gap-x-3 px-3 pb-2 mb-3 border-b border-slate-200 text-[11px] font-mono uppercase tracking-wider text-slate-500" data-testid="commentaires-entete">
                <label className="flex items-center gap-2"><Checkbox checked={tous} onCheckedChange={(v) => setCoches(v ? data.operations.map((o) => o.id) : [])} data-testid="commentaires-tout-cocher" />Date</label><span className="text-center">Rang</span><span>Description</span><span className="text-right">Retrait</span><span className="text-right">Dépôt</span><span className="text-right">TPS</span><span className="text-right">TVQ</span><span className="text-right leading-tight">Taxes</span>
              </div>
              {data.operations.map((op, i) => <OperationBlock key={op.id} op={op} index={i} comptes={plan.comptes} categories={choix.categories} logements={choix.logements} onSaved={onSaved} coche={coches.includes(op.id)} onCoche={onCoche} nbSemblables={(parCle[op.cle] || []).length} onSemblables={onSemblables} />)}
            </>}
        </Panel>
      )}
      {data && data.operations.length > 0 && <ResumeCommentaires resume={data.resume} />}
      <div className="mt-6"><DoublonsPanel version={version} onChange={() => { charger(false); setVersion((v) => v + 1); }} /></div>
      <div className="mt-6"><ConciliationTaxesPanel key={`${version}-${vConc}`} onChange={() => setVSuivi((v) => v + 1)} /></div>
      <div className="mt-6"><TropPayesPanel version={`${version}-${vSuivi}`} onChange={() => setVConc((v) => v + 1)} /></div>
      <div className="mt-6"><AcomptesTaxesPanel version={`${version}-${vSuivi}-${vConc}`} /></div>
      <div className="mt-6"><EtatsRqPanel version={`${version}-${vSuivi}-${vConc}`} /></div>
      <div className="mt-6"><LoyersManquantsPanel version={version} /></div>
      <div className="mt-6"><RecusLoyerPanel version={version} /></div>
      <div className="mt-6"><FournisseursPanel logements={choix.logements} version={version} onChange={() => { charger(false); setVersion((v) => v + 1); }} /></div>
      <div className="mt-6"><PayeursPanel version={version} onChange={() => { charger(false); setVersion((v) => v + 1); }} /></div>
      <div className="mt-6"><LocatairesPanel version={version} onChange={() => { charger(false); setVersion((v) => v + 1); }} /></div>
    </div>
  );
}
